<template>
  <div class="columns-setting-wrapper">
    <!-- setting header -->
    <div class="columns-setting-header">
      <span class="column-name column-first">表头名称</span>
      <span class="column-fixed">锁定列</span>
      <span class="column-hidden">是否显示</span>
      <span class="column-sort">排序</span>
    </div>

    <!-- setting content -->
    <draggable :list="list" class="draggable-content" @end="changeColumns(true)">
      <div v-for="(item, index) in list" :key="index" class="draggable-item">
        <div class="draggable-item-unit column-name ellipsis-warp">
          <svg-icon icon-class="move"></svg-icon>
          <el-tooltip class="item" effect="dark" :content="item.label" placement="top">
            <span class="column-label">{{ item.label }}</span>
          </el-tooltip>
        </div>
        <div class="draggable-item-unit column-fixed column-fixed-svg">
          <svg-icon :icon-class="columnFixed(item, 'left')" @click="handleFixedColumn(item, 'left')"></svg-icon>
          <svg-icon :icon-class="columnFixed(item, 'right')" @click="handleFixedColumn(item, 'right')"></svg-icon>
        </div>
        <div class="draggable-item-unit column-hidden">
          <el-checkbox v-model="item.checked" size="mini" :disabled="item.disabled || (list.length < 2 && item.checked)" @change="toggleColumnHidden(item)" />
          <span class="column-hidden-label">显示</span>
        </div>
        <div class="draggable-item-unit column-sort column-fixed-svg">
          <svg-icon :icon-class="columnSort(item, true)" @click="handleSortColumn(item, true)"></svg-icon>
          <svg-icon :icon-class="columnSort(item, false)" @click="handleSortColumn(item, false)"></svg-icon>
        </div>
      </div>
    </draggable>

    <!-- setting footer -->
    <div class="columns-setting-footer">
      <el-button size="mini" type="text" @click="resetColumns">恢复默认</el-button>
      <el-button type="primary" size="mini" @click="applyColumns">应用</el-button>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { cloneDeep, isEqual } from "lodash";
import ColumnApi from "@/api/general-table";

export default {
  name: "ColumnsSetting",
  components: { draggable },
  props: {
    columns: {
      type: Array,
      default: () => []
    },
    code: [String, Number],
    version: [String, Number],
    // 自定义排序
    customSort: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      list: [],
      sort: [],
      sortInit: null
    };
  },
  computed: {
    columnFixed() {
      return (item, direction) => {
        if (direction === "left") {
          if (item.fixed === true || item.fixed === "true" || item.fixed === "left") {
            return "locked_left";
          }

          return "unlock_left";
        }

        if (item.fixed === "right") {
          return "locked_right";
        }

        return "unlock_right";
      };
    },
    columnSort() {
      return (current, isBoolean) => {
        const findData = this.sort.find(item => item.column === `${current.key || current.name}`);

        if (findData) {
          if (isBoolean) {
            if (findData.asc) {
              return "sort_up_select";
            } else {
              return "sort_up";
            }
          }

          if (!findData.asc) {
            return "sort_down_select";
          }

          return "sort_down";
        }

        if (isBoolean) {
          return "sort_up";
        }

        return "sort_down";
      };
    }
  },
  watch: {
    columns: {
      deep: true,
      immediate: true,
      handler(newValue) {
        // 生成虚拟属性checked
        newValue = newValue.map(item => {
          item.checked = !item.hidden;
          return item;
        });

        if (isEqual(newValue, this.list)) {
          return;
        }

        this.list = cloneDeep(newValue);
      }
    },
    customSort: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (!this.sortInit) {
          this.sortInit = newValue;
        }
        if (isEqual(newValue, this.sort)) {
          return;
        }

        this.sort = cloneDeep(newValue);
      }
    }
  },
  methods: {
    // 修改columns
    changeColumns(drag = false) {
      // 拖拽后的列变更
      this.$emit("changeColumns", this.list);
      // 拖拽根据表格列重新排列顺序
      if (drag) {
        this.changeSortByColumn();
        this.$emit("changeCustomSort", this.sort);
      }
    },
    // 切换隐藏
    toggleColumnHidden(item) {
      item.hidden = !item.hidden;
      this.changeColumns();
    },
    // 修改左右固定
    handleFixedColumn(item, direction = "left") {
      item.fixed = item.fixed === direction ? false : direction;
      this.changeColumns();
    },
    handleSortColumn(data, asc = true) {
      const key = data.key || data.name;
      // eslint-disable-next-line no-shadow
      const item = this.sort.find(item => item.column === key);

      if (item) {
        // 取消排序
        if (item.asc === asc) {
          // eslint-disable-next-line no-shadow
          this.sort.splice(this.sort.findIndex(item => item.column === key), 1);
        } else {
          item.asc = asc;
        }
      } else {
        this.sort.push({ column: key, asc });
      }

      // 根据表格列重新排列顺序
      this.changeSortByColumn();
      this.$emit("changeCustomSort", this.sort);
    },
    changeSortByColumn() {
      const sortList = [];
      this.list.forEach(item => {
        const key = item.key || item.name;
        // eslint-disable-next-line no-shadow
        const find = this.sort.find(item => item.column === key);
        find && sortList.push({ column: find.column, asc: find.asc });
      });

      this.sort = sortList;
    },
    // 重置
    resetColumns() {
      this.$emit("resetColumns");
      this.$emit("changeCustomSort", this.sortInit);
    },
    // 应用
    applyColumns() {
      this.$emit("applyColumns", this.list);
      // 保存数据到服务端
      this.saveData();
      this.saveSortData();
    },
    saveData() {
      const columnList = this.list.map(col => ({
        displayName: col.label,
        name: col.key,
        fixed: col.fixed,
        hidden: col.hidden,
        sortable: col.sortable,
        width: col.width
      }));
      ColumnApi.saveFormColumn(this.code, { columnShow: columnList, formVersion: this.version });
    },
    saveSortData() {
      ColumnApi.saveOrder(this.code, this.sort);
    }
  }
};
</script>

<style lang="scss">
  $border-color: #D6D6D8;
  $background-color: #F5F7FA;

  .ellipsis-warp {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .columns-setting-wrapper {
    width: 400px;
    padding: 18px 18px 0;

    .columns-setting-header {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0 6px;
      border-top: 1px solid $border-color;
      background-color: $background-color;
    }

    .column-name {
      flex: 30%;
    }

    .column-fixed {
      flex: 25%;
    }

    .column-hidden {
      flex: 25%;
    }

    .column-sort {
      flex: 20%;
    }

    .column-first {
      padding-left: 20px;
    }

    .draggable-content {
      max-height: 200px;
      overflow-y: auto;
    }

    .draggable-item {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0 6px;
      cursor: move;
      border-top: 1px dashed $border-color
    }

    .draggable-item-unit {
      display: inline-block;
      text-align: left;

      .column-label {
        margin-left: 6px;
      }

      .column-hidden-label {
        margin-left: 10px;
      }
    }

    .column-fixed-svg {
      font-size: 30px;
      cursor: pointer;
    }

    .columns-setting-footer {
      border-top: 1px solid $border-color;
      height: 60px;
      line-height: 60px;
      text-align: right;
    }
  }
</style>
